import { z } from "zod";
import type { holidayCalendarState } from "@/lib/submission/holidays.server";
import { queryOptions } from "@tanstack/react-query";
import { localDay } from "@/lib/submission/rules";
import { submissionYearBounds } from "@/lib/submission/calendar";
import type { SubmissionJournalOrder } from "@/lib/submission/preferences";
import type {
  adminState,
  submissionState,
  dayDetail,
  MemberInput,
  submissionHistory,
  submissionLeaderboard,
} from "@/lib/submission/service.server";
import type { LeaderboardPeriod } from "@/lib/submission/check-ins";

type Json<T> = T extends Date
  ? string
  : T extends Array<infer V>
    ? Json<V>[]
    : T extends object
      ? { [K in keyof T]: Json<T[K]> }
      : T;

export type SubmissionHolidays = Json<Awaited<ReturnType<typeof holidayCalendarState>>>;

export type SubmissionState = Json<Awaited<ReturnType<typeof submissionState>>>;

export type SubmissionAdmin = Json<Awaited<ReturnType<typeof adminState>>>;

export type SubmissionDetail = Json<Awaited<ReturnType<typeof dayDetail>>>;

export type SubmissionYear = Json<Awaited<ReturnType<typeof submissionHistory>>>;

export type SubmissionLeaderboard = Json<Awaited<ReturnType<typeof submissionLeaderboard>>>;

export type SubmissionMember = SubmissionAdmin["members"][number];

export type { MemberInput };

export async function submissionRequest<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const parsed = z.object({ message: z.string() }).safeParse(body);
    const message = parsed.success ? parsed.data.message : "Opération impossible. Réessayez.";
    throw new Error(message);
  }

  // SAFETY: these types describe the JSON-serialized responses of our defineRoute endpoints.
  return response.json() as Promise<T>;
}

export function submissionQueryOptions(before?: string) {
  return queryOptions({
    queryKey: ["submission", "days", before],
    queryFn: () =>
      submissionRequest<SubmissionState>(
        `/api/soumission${before ? `?before=${encodeURIComponent(before)}` : ""}`,
      ),
    refetchInterval: 30_000,
  });
}

export function submissionDetailOptions(date: string) {
  return queryOptions({
    queryKey: ["submission", "detail", date],
    queryFn: () =>
      submissionRequest<SubmissionDetail>(`/api/soumission/${encodeURIComponent(date)}`),
    enabled: Boolean(date),
    // Only today's deliveries still move; a past day is final once loaded.
    refetchInterval: (query) =>
      query.state.data?.day.date === localDay(new Date()) ? 30_000 : false,
  });
}

export const submissionAdminOptions = queryOptions({
  queryKey: ["submission", "admin"],
  queryFn: () => submissionRequest<SubmissionAdmin>("/api/admin/soumission"),
});

export const submissionPreferencesOptions = queryOptions({
  queryKey: ["submission", "preferences"],
  queryFn: () =>
    submissionRequest<{ ordre: SubmissionJournalOrder }>("/api/user/soumission-preferences"),
});

export function submissionHistoryOptions(year: number) {
  const { from, until } = submissionYearBounds(year);

  return queryOptions({
    queryKey: ["submission", "history", year],
    queryFn: () => submissionRequest<SubmissionYear>(`/api/soumission/history?year=${year}`),
    refetchInterval: (query) =>
      query.state.data && query.state.data.today >= from && query.state.data.today < until
        ? 30_000
        : false,
  });
}

export function submissionLeaderboardOptions(period: LeaderboardPeriod, enabled = true) {
  return queryOptions({
    queryKey: ["submission", "leaderboard", period],
    queryFn: () =>
      submissionRequest<SubmissionLeaderboard>(`/api/soumission/classement?periode=${period}`),
    enabled,
  });
}

type SubmissionWrite =
  | { ordre: SubmissionJournalOrder }
  | { action: "skip"; date: string }
  | { action: "validate"; date: string }
  | { date: string }
  | { enabled: boolean; revision: string; startsOn?: string | null }
  | { revision: string; notificationsPausedUntil: string | null }
  | { date: string; verificationRequired: boolean }
  | { year: number }
  | { revision: string; members: MemberInput[] };

export function jsonRequest(method: string, body: SubmissionWrite): RequestInit {
  return { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) };
}

export function submissionHolidaysOptions(year: number) {
  return queryOptions({
    queryKey: ["submission", "holidays", year],
    queryFn: () =>
      submissionRequest<SubmissionHolidays>(`/api/admin/soumission/holidays?year=${year}`),
  });
}
